/**
 * CLUE REVEAL STATE — which individual discovery CONDITIONS the player has uncovered.
 *
 * A clue pickup no longer reveals a whole species at once: each note reveals exactly ONE condition bullet
 * (see clueText.ts). So the persisted sets (`revealedFish` / `revealedBugs`) hold per-condition entries
 * encoded as `"<speciesId>:<condKey>"` rather than bare species ids.
 *
 * GRANDFATHERING (deliberate, and the reason `ALL` exists). Saves written before this change hold BARE
 * species ids, meaning "this player already caught a bottle for this species and could read everything about
 * it". Re-locking that knowledge would be taking away progress the player earned, so a bare id is migrated to
 * the wildcard `"<id>:*"` — every condition legible, forever. New reveals are always specific keys, so the
 * one-per-note rule applies from here on without disturbing anyone's existing log.
 *
 * Encoding note: condition keys can themselves contain ':' (e.g. `species:rose`, `cluster:daisy`), so entries
 * are split on the FIRST colon only. Species ids never contain one.
 */

/** The wildcard condition key meaning "every condition on this species is legible" (see grandfathering). */
export const ALL = "*";

/** Encode one revealed condition for persistence. */
export function revealKey(speciesId: string, condKey: string): string {
  return `${speciesId}:${condKey}`;
}

/** Split a persisted entry back into its species id and condition key. Splits on the FIRST colon. */
export function parseReveal(entry: string): { id: string; cond: string } | null {
  const i = entry.indexOf(":");
  if (i <= 0) return null;
  return { id: entry.slice(0, i), cond: entry.slice(i + 1) };
}

/**
 * Coerce a persisted reveal array into the per-condition format, migrating old bare-id entries.
 * `isKnownId` filters out species that no longer exist in the roster.
 */
export function coerceReveals(raw: unknown, isKnownId: (id: string) => boolean): string[] {
  if (!Array.isArray(raw)) return [];
  const out = new Set<string>();
  for (const v of raw) {
    if (typeof v !== "string" || !v) continue;
    const parsed = parseReveal(v);
    if (!parsed) {
      // A BARE species id — a pre-per-condition save. Grandfather it to "everything revealed".
      if (isKnownId(v)) out.add(revealKey(v, ALL));
      continue;
    }
    if (isKnownId(parsed.id)) out.add(v);
  }
  return [...out];
}

/** The condition keys the player has revealed for one species. `ALL` present ⇒ everything is legible. */
export function revealedCondKeys(reveals: string[], speciesId: string): string[] {
  const out: string[] = [];
  for (const entry of reveals) {
    const p = parseReveal(entry);
    if (p && p.id === speciesId) out.push(p.cond);
  }
  return out;
}

/** True when every condition on `speciesId` is legible (the grandfathered wildcard). */
export function hasAllRevealed(reveals: string[], speciesId: string): boolean {
  return revealedCondKeys(reveals, speciesId).includes(ALL);
}

/** Which of `condKeys` are legible for this species — wildcard expands to all of them. */
export function legibleKeys(reveals: string[], speciesId: string, condKeys: string[]): string[] {
  const known = revealedCondKeys(reveals, speciesId);
  if (known.includes(ALL)) return [...condKeys];
  return condKeys.filter((k) => known.includes(k));
}

/** True when the player has uncovered at least one condition for this species (drives the guide's clue badge). */
export function hasAnyReveal(reveals: string[], speciesId: string): boolean {
  return reveals.some((e) => { const p = parseReveal(e); return p != null && p.id === speciesId; });
}

/** The species ids the player has ANY clue for. */
export function revealedSpeciesIds(reveals: string[]): string[] {
  const out = new Set<string>();
  for (const e of reveals) { const p = parseReveal(e); if (p) out.add(p.id); }
  return [...out];
}
